/**
 * Logo component that displays the company logo with optional wordmark.
 *
 * @format
 * @param {Object} props - The props object.
 * @param {boolean} [props.colorInvert=false] - Whether to invert the color of the logo.
 * @param {number} [props.zoom] - The zoom level of the logo.
 * @param {string} [props.link] - The link to redirect to when the logo is clicked.
 * @param {boolean} [props.wordmark=false] - Whether to display the wordmark.
 * @returns {JSX.Element} - The Logo component.
 */

import { Box, Stack } from "@mui/material";
import isSSR from "isssr";

// Import new image assets
import IconDark from "./assets/icon-dark.png";
import IconLight from "./assets/icon-light.png";
import WordmarkDark from "./assets/wordmark-dark.png";
import WordmarkLight from "./assets/wordmark-light.png";

// Import Icon component
import Icon from "./components/Icon";

// The logo component
const Logo = ({ colorInvert = false, zoom, link = true, onClick = null, wordmark = false, hover = true }) => {
	// Set up navigation onClick handler
	if (link && !onClick) {
		onClick = () => {
			if (!isSSR) document.location.href = "/";
		};
	} else if (!onClick) {
		onClick = () => {};
	}

	try {
		return (
			<Box
				onClick={onClick}
				role="presentation"
				sx={{
					textDecoration: "none",
					cursor: link ? "pointer" : "default",
					opacity: hover ? 0.8 : 1,
					transition: "opacity 0.2s ease",
					":hover": { cursor: "pointer", opacity: 1 },
				}}
				data-testid="logo"
			>
				<Stack
					direction="row"
					justifyContent="center"
					alignItems="center"
					spacing={2}
					sx={{ zoom: zoom ? zoom * 100 + "%" : "100%" }}
				>
					{!wordmark ? (
						// Only show the icon when wordmark is false
						<Box
							component="img"
							alt="Made Live Logo"
							src={!colorInvert ? IconLight : IconDark}
							sx={{ width: "40px", height: "auto" }}
						/>
					) : (
						// Show the wordmark when wordmark is true
						<Box
							component="img"
							alt="Made Live Logo with Wordmark"
							src={!colorInvert ? WordmarkLight : WordmarkDark}
							sx={{ width: "auto", maxWidth: "200px", height: "auto" }}
						/>
					)}
				</Stack>
			</Box>
		);
	} catch (error) {
		console.error("Error rendering Logo:", error);
		return null;
	}
};

export default Logo;
export { Icon, IconDark, IconLight, WordmarkDark, WordmarkLight };
